/* eslint-disable max-lines */
import * as React from 'react';

import { numberToPrettyString } from '@cian/utils/lib/shared/number_helpers';

import { IBusinessShoppingCenterSchema, IOfferSchema } from '@cian/api-models/offer-card/v1/get-offer-data';
import { IconQuestion } from '../../common/components/icon_question';
import { WcType } from '../../store/offer';
import { isArrayWithItems, isDefined } from '../../utils';
import { FDealType, getDealType, isRent } from '../../utils/deal_type';
import { CurrencySymbol } from '../../utils/formatters';
import { getLandAreaUnitType } from '../../utils/offer_helpers';
import {
  FOfferType,
  getOfferType,
  isCommercial,
  isFlat,
  isResidential,
  isSuburbanResidential,
  isUrban,
} from '../../utils/offer_type';
import { AdditionalFeaturesGroup } from './group';
import { AdditionalFeatureItem } from './item';
import {
  AccessTypeNames,
  BSCInputTypeNames,
  BuildingType,
  ConditionTypeNames,
  CranageTypeNames,
  DecorationType,
  FloorMaterialNames,
  FurniturePresenceNames,
  GarageCategoryNames,
  GarageMaterialNames,
  GarageStatusNames,
  GatesTypeNames,
  InputTypeNames,
  LandStatus,
  LayoutNames,
  ParkingLocationTypeNames,
  ParkingPurposeTypeNames,
  ParkingTypeNames,
  PermittedUseTypeNames,
  PlacementTypeNames,
  RepairType,
  RoomType,
  WindowsViewType,
} from './locales';

import * as styles from './index.css';
import * as itemStyles from './item/index.css';

export interface IAdditionalFeaturesCommercialProps {
  businessShoppingCenter: IBusinessShoppingCenterSchema | null;
  offer: IOfferSchema;
}

export class AdditionalFeaturesCommercial extends React.PureComponent<IAdditionalFeaturesCommercialProps, {}> {
  public render() {
    const { offer } = this.props;

    const dealType = getDealType(offer);
    const offerType = getOfferType(offer);

    if (isResidential(offerType)) {
      return (
        <section className={styles['container']}>
          <AdditionalFeaturesGroup>
            {dealType === FDealType.Sale && isUrban(offerType) && (
              <AdditionalFeatureItem
                name="Тип жилья"
                value={offerType === FOfferType.FlatNew ? 'Новостройка' : 'Вторичка'}
              />
            )}
            {Boolean(offer.isApartments || offer.isPenthouse) && (
              <AdditionalFeatureItem name="Статус" value={offer.isApartments ? 'Апартаменты' : 'Пентхаус'} />
            )}
            {offer.land && offer.land.status && (
              <AdditionalFeatureItem name="Статус участка" value={LandStatus[offer.land.status]} />
            )}
            {offer.roomsCount && (
              <AdditionalFeatureItem
                name={
                  isFlat(offerType) || offerType === FOfferType.FlatShared
                    ? 'Количество комнат'
                    : 'Всего комнат в квартире'
                }
                value={offer.roomsCount}
              />
            )}
            {Boolean(offer.bedsCount) && (
              <AdditionalFeatureItem name="Количество спальных мест" value={Number(offer.bedsCount)} />
            )}
            {Boolean(offer.roomsForSaleCount) && (
              <AdditionalFeatureItem
                name={`Комнат в ${isRent(dealType) ? 'аренду' : 'продажу'}`}
                value={Number(offer.roomsForSaleCount)}
              />
            )}
            {offer.roomType && <AdditionalFeatureItem name="Тип комнаты" value={RoomType[offer.roomType]} />}
            {Boolean(offer.roomArea) && (
              <AdditionalFeatureItem
                name="Площадь комнаты"
                value={`${numberToPrettyString(Number(offer.roomArea))} м\u00B2`}
              />
            )}
            {offer.allRoomsArea && (
              <li className={itemStyles['item']}>
                <span className={itemStyles['name']}>
                  Площадь комнат
                  <span className={styles['helper-wrapper']}>
                    <IconQuestion />
                    <div className={styles['tooltip']}>
                      + обозначение смежных комнат
                      <br />- обозначение изолированных комнат
                    </div>
                  </span>
                </span>
                <span className={itemStyles['value']}>{`${offer.allRoomsArea} м\u00B2`}</span>
              </li>
            )}
            {offer.land && offer.land.area && (
              <AdditionalFeatureItem
                name="Площадь участка"
                // tslint:disable-next-line:max-line-length
                value={`${numberToPrettyString(Number(offer.land.area))} ${getLandAreaUnitType(offer.land)}`}
              />
            )}
            {offer.floorNumber && <AdditionalFeatureItem name="Этаж" value={offer.floorNumber} />}
            {offer.building && offer.building.floorsCount && (
              <AdditionalFeatureItem name="Этажей в доме" value={offer.building.floorsCount} />
            )}
            {offer.building && offer.building.materialType && (
              <AdditionalFeatureItem name="Тип дома" value={BuildingType[offer.building.materialType]} />
            )}
            {offer.building && Boolean(offer.building.ceilingHeight) && (
              <AdditionalFeatureItem
                name="Высота потолков"
                value={`${numberToPrettyString(Number(offer.building.ceilingHeight))} м`}
              />
            )}
            {offer.shareAmount && offerType === FOfferType.FlatShared && (
              <AdditionalFeatureItem name="Размер доли" value={offer.shareAmount.replace('.', ',')} />
            )}
            {offer.shareAmount && isSuburbanResidential(offerType) && (
              <AdditionalFeatureItem
                name={isRent(dealType) ? 'Часть в аренду' : 'Часть в продажу'}
                value={offer.shareAmount.replace('.', ',')}
              />
            )}
            {((offer.wcType && offer.wcType === WcType.Combined) ||
              (isDefined<number>(offer.combinedWcsCount) && offer.combinedWcsCount > 0)) && (
              <AdditionalFeatureItem name="Совмещённый санузел" value={offer.combinedWcsCount || 'Есть'} />
            )}
            {((offer.wcType && offer.wcType === WcType.Separate) ||
              (isDefined<number>(offer.separateWcsCount) && offer.separateWcsCount > 0)) && (
              <AdditionalFeatureItem name="Раздельный санузел" value={offer.separateWcsCount || 'Есть'} />
            )}
            {offer.hasBathtub && <AdditionalFeatureItem name="Ванная комната" value="Есть" />}
            {offer.repairType && <AdditionalFeatureItem name="Ремонт" value={RepairType[offer.repairType]} />}
            {offer.windowsViewType && (
              <AdditionalFeatureItem name="Вид из окон" value={WindowsViewType[offer.windowsViewType]} />
            )}
            {offer.decoration && <AdditionalFeatureItem name="Отделка" value={DecorationType[offer.decoration]} />}
          </AdditionalFeaturesGroup>
        </section>
      );
    } else if (isCommercial(offerType)) {
      const { businessShoppingCenter } = this.props;

      return (
        <section className={styles['container']}>
          <AdditionalFeaturesGroup>
            {offer.placementType && (
              <AdditionalFeatureItem name="Тип помещения" value={PlacementTypeNames[offer.placementType]} />
            )}
            {offer.taxNumber !== null && <AdditionalFeatureItem name="Номер налоговой" value={offer.taxNumber} />}
            {offer.isLegalAddressProvided !== null && (
              <AdditionalFeatureItem
                name="Юридический адрес"
                value={offer.isLegalAddressProvided ? 'Предоставляется' : 'Не предоставляется'}
              />
            )}
            {offer.building && !!offer.building.ceilingHeight && (
              <AdditionalFeatureItem
                name="Высота потолков"
                value={`${numberToPrettyString(Number(offer.building.ceilingHeight))} м`}
              />
            )}
            {offer.hasShopWindows && <AdditionalFeatureItem name="Витринные окна" value="Есть" />}
            {offer.layout && <AdditionalFeatureItem name="Планировка" value={LayoutNames[offer.layout]} />}
            {(offer.inputType && <AdditionalFeatureItem name="Вход" value={InputTypeNames[offer.inputType]} />) ||
              (businessShoppingCenter && businessShoppingCenter.inputType && (
                <AdditionalFeatureItem name="Вход" value={BSCInputTypeNames[businessShoppingCenter.inputType]} />
              )) ||
              null}
            {offer.conditionType && (
              <AdditionalFeatureItem name="Состояние" value={ConditionTypeNames[offer.conditionType]} />
            )}
            {offer.building && offer.building.gatesType && (
              <AdditionalFeatureItem name="Ворота" value={GatesTypeNames[offer.building.gatesType]} />
            )}
            {offer.floorMaterialType && (
              <AdditionalFeatureItem name="Материал пола" value={FloorMaterialNames[offer.floorMaterialType]} />
            )}
            {(offer.furniturePresence || offer.hasFurniture !== null) && (
              <AdditionalFeatureItem
                name="Мебель"
                value={
                  (offer.furniturePresence && FurniturePresenceNames[offer.furniturePresence]) ||
                  (offer.hasFurniture ? 'Есть' : 'Нет')
                }
              />
            )}
            {offer.waterPipesCount !== null && (
              <AdditionalFeatureItem name="Количество мокрых точек" value={offer.waterPipesCount} />
            )}
            {(offer.electricityPower !== null && (
              <AdditionalFeatureItem name="Мощность, кВт" value={offer.electricityPower} />
            )) ||
              (businessShoppingCenter &&
                businessShoppingCenter.electricity &&
                businessShoppingCenter.electricity.power !== null && (
                  <AdditionalFeatureItem name="Мощность, кВт" value={businessShoppingCenter.electricity.power} />
                )) ||
              (offer.electricity && offer.electricity.power !== null && (
                <AdditionalFeatureItem name="Мощность, кВт" value={offer.electricity.power} />
              )) ||
              null}
            {offer.accessType && <AdditionalFeatureItem name="Доступ" value={AccessTypeNames[offer.accessType]} />}
            {offer.hasEquipment !== null && (
              <AdditionalFeatureItem name="Оборудование" value={offer.hasEquipment ? 'Есть' : 'Нет'} />
            )}
          </AdditionalFeaturesGroup>
          {(offer.hasInvestmentProject !== null || offer.hasEncumbrances !== null) && (
            <AdditionalFeaturesGroup>
              {offer.hasInvestmentProject !== null && (
                <AdditionalFeatureItem name="Инвестпроект" value={offer.hasInvestmentProject ? 'Есть' : 'Нет'} />
              )}
              {offer.hasEncumbrances !== null && (
                <AdditionalFeatureItem name="Обременение" value={offer.hasEncumbrances ? 'Есть' : 'Нет'} />
              )}
            </AdditionalFeaturesGroup>
          )}
          {offer.permittedUseType && (
            <AdditionalFeaturesGroup>
              <AdditionalFeatureItem name="ВРИ" value={PermittedUseTypeNames[offer.permittedUseType]} />
              {offer.possibleToChangePermittedUseType !== null && (
                <AdditionalFeatureItem
                  name="Можно изменить"
                  value={offer.possibleToChangePermittedUseType ? 'Да' : 'Нет'}
                />
              )}
            </AdditionalFeaturesGroup>
          )}
          {offer.garage && (
            <AdditionalFeaturesGroup>
              {offer.garage.garageType && (
                <AdditionalFeatureItem name="Тип гаража" value={GarageCategoryNames[offer.garage.garageType]} />
              )}
              {offer.garage.material && (
                <AdditionalFeatureItem name="Тип бокса" value={GarageMaterialNames[offer.garage.material]} />
              )}
              {offer.garage.status && (
                <AdditionalFeatureItem name="Статус" value={GarageStatusNames[offer.garage.status]} />
              )}
            </AdditionalFeaturesGroup>
          )}
          {offer.building && offer.building.parking && (
            <AdditionalFeaturesGroup>
              {offer.building.parking.type && (
                <AdditionalFeatureItem name="Парковка" value={ParkingTypeNames[offer.building.parking.type]} />
              )}
              {offer.building.parking.locationType && (
                <AdditionalFeatureItem
                  name="Парковка"
                  value={ParkingLocationTypeNames[offer.building.parking.locationType]}
                />
              )}
              {offer.building.parking.purposeType && (
                <AdditionalFeatureItem
                  name="Тип парковки"
                  value={ParkingPurposeTypeNames[offer.building.parking.purposeType]}
                />
              )}
              {offer.building.parking.placesCount !== null && (
                <AdditionalFeatureItem name="Количество мест" value={offer.building.parking.placesCount} />
              )}
              {offer.building.parking.isFree ||
                (offer.building.parking.pricesMonthly !== null && (
                  <AdditionalFeatureItem
                    name="Стоимость"
                    value={
                      offer.building.parking.isFree || offer.building.parking.pricesMonthly.rur === 0
                        ? 'Бесплатно'
                        : `${numberToPrettyString(offer.building.parking.pricesMonthly.rur)} ${
                            CurrencySymbol.Rur
                          }/место`
                    }
                  />
                ))}
              {!offer.building.parking.isFree && offer.building.parking.pricesEntry !== null && (
                <AdditionalFeatureItem
                  name="Стоимость въезда"
                  value={
                    offer.building.parking.pricesEntry.rur === 0
                      ? 'Бесплатно'
                      : `${numberToPrettyString(offer.building.parking.pricesEntry.rur)} ${CurrencySymbol.Rur}`
                  }
                />
              )}
            </AdditionalFeaturesGroup>
          )}
          {businessShoppingCenter &&
            businessShoppingCenter.parkings &&
            businessShoppingCenter.parkings.map((parking, index) => (
              <AdditionalFeaturesGroup key={`AdditionalFeaturesGroup_${index}`}>
                {parking.type && <AdditionalFeatureItem name="Парковка" value={ParkingTypeNames[parking.type]} />}
                {parking.placesCount !== null && (
                  <AdditionalFeatureItem name="Количество мест" value={parking.placesCount} />
                )}
                {(parking.isFree || parking.priceMonthly !== null) && (
                  <AdditionalFeatureItem
                    name="Стоимость"
                    value={parking.isFree ? 'Бесплатно' : `${parking.priceMonthly} ${CurrencySymbol.Rur}`}
                  />
                )}
              </AdditionalFeaturesGroup>
            ))}
          {offer.building && isArrayWithItems(offer.building.cranageTypes) && (
            <AdditionalFeaturesGroup header="Крановое оборудование">
              {offer.building.cranageTypes.map((cranage, index) => (
                <AdditionalFeatureItem
                  key={`cranage_${index}`}
                  name={CranageTypeNames[cranage.type]}
                  value={[
                    cranage.count ? `${numberToPrettyString(cranage.count)}\u00a0шт.` : null,
                    cranage.loadCapacity ? `${numberToPrettyString(cranage.loadCapacity)}\u00a0т.` : null,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                />
              ))}
            </AdditionalFeaturesGroup>
          )}
          {(offer.isCustoms || offer.hasTransportServices || offer.hasSafeCustody) && (
            <AdditionalFeaturesGroup>
              <AdditionalFeatureItem
                name="Дополнительные услуги"
                value={[
                  (offer.isCustoms && 'Таможня') || null,
                  (offer.hasTransportServices && 'Транспортные услуги') || null,
                  (offer.hasSafeCustody && 'Ответственное хранение') || null,
                ]
                  .filter(a => !!a)
                  .join(', ')}
              />
            </AdditionalFeaturesGroup>
          )}
        </section>
      );
    }

    return null;
  }
}
